import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import { useHospitalProfile } from '../hooks/useHospital';
import hospitalService from '../services/hospitalService';
import { Mail, Phone, User, MapPin, Building2, Loader2 } from 'lucide-react';
import { FloatingNavBar } from './Navbar';

const COUNTRY_CODES = ['+91', '+1', '+44', '+61', '+81', '+86', '+49', '+33', '+971', '+65', '+55', '+39', '+34', '+7', '+82', '+31', '+41', '+46', '+47'];

const HospitalProfileEdit = ({ onBackToProfile }) => {
  const { user: authUser } = useAuth();
  const { data: profileData, loading: profileLoading, error: profileError } = useHospitalProfile();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    phone_country_code: '+91',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contactPerson: '',
    contactPersonPhone: '',
    contactPersonPhone_country_code: '+91',
    contactPersonEmail: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Map API response (GET /v1/hospital/my-profile snake_case) to form (request body keys)
  const mapProfileToForm = (data) => {
    if (!data) {
      return {
        name: '',
        phone: '',
        phone_country_code: '+91',
        address: '',
        city: '',
        state: '',
        pincode: '',
        contactPerson: '',
        contactPersonPhone: '',
        contactPersonPhone_country_code: '+91',
        contactPersonEmail: ''
      };
    }
    // Hospital phone: API may send hospital_user_phone or only contact_person_phone
    const phoneRaw = data.hospital_user_phone ?? data.contact_person_phone ?? data.phone ?? '';
    const contactPhoneRaw = data.contact_person_phone ?? '';
    return {
      name: data.name ?? '',
      phone: String(phoneRaw).replace(/\D/g, '').slice(-10),
      phone_country_code: data.phone_country_code ?? '+91',
      address: data.address ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      pincode: data.pincode != null ? String(data.pincode) : '',
      contactPerson: data.contact_person ?? '',
      contactPersonPhone: String(contactPhoneRaw).replace(/\D/g, '').slice(-10),
      contactPersonPhone_country_code: data.contact_person_phone_country_code ?? '+91',
      contactPersonEmail: data.contact_person_email ?? ''
    };
  };

  useEffect(() => {
    if (profileData?.success && profileData?.data) {
      setFormData(mapProfileToForm(profileData.data));
    } else if (authUser) {
      setFormData(prev => ({
        ...prev,
        name: authUser.name ?? prev.name,
        contactPerson: authUser.name ?? prev.contactPerson,
        contactPersonEmail: authUser.email ?? prev.contactPersonEmail
      }));
    }
  }, [profileData, authUser]);

  const normalizePhone = (value) => {
    const digits = (value || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;
    if (field === 'phone' || field === 'contactPersonPhone') {
      processedValue = normalizePhone(value);
    }
    if (field === 'pincode') {
      processedValue = (value || '').replace(/\D/g, '').slice(0, 6);
    }
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (saveError) setSaveError(null);
    if (saveSuccess) setSaveSuccess(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const body = {
        name: formData.name,
        phone: formData.phone,
        phone_country_code: formData.phone_country_code,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        contactPerson: formData.contactPerson,
        contactPersonPhone: formData.contactPersonPhone,
        contactPersonPhone_country_code: formData.contactPersonPhone_country_code,
        contactPersonEmail: formData.contactPersonEmail
      };

      const result = await hospitalService.updateMyProfile(body);

      if (result.success) {
        setSaveSuccess(true);
        if (result.data) {
          setFormData(mapProfileToForm(result.data));
        }
      } else {
        throw new Error(result.message || 'Failed to update profile');
      }
    } catch (error) {
      if (error.errors && Array.isArray(error.errors)) {
        setSaveError(error.errors.map(e => `${e.field}: ${e.message}`).join(', '));
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setSaveError(error.response.data.errors.map(e => `${e.field}: ${e.message}`).join(', '));
      } else if (error.response?.data?.message) {
        setSaveError(error.response.data.message);
      } else if (error.message) {
        setSaveError(error.message);
      } else {
        setSaveError('Failed to update profile. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = 'w-full pl-10 pr-4 py-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 outline-none transition-all';
  const labelClass = 'block text-sm font-medium text-slate-600';
  const selectClass = 'w-full px-4 py-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 outline-none transition-all';

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (profileError && !authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load profile</p>
          <p className="text-sm text-gray-600">{profileError.message || 'Please try again later'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-8xl mx-auto px-4 py-8 flex flex-col items-center">
        {/* Single card: glassmorphism, clearly visible border, rounded corners */}
        <div className="w-full rounded-2xl  backdrop-blur-xl border-2 border-slate-300 shadow-xl shadow-slate-200/50 overflow-hidden">
          {/* Card header */}
          <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-slate-100">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-800 tracking-tight">Update Profile</h1>
            <p className="mt-1 text-sm text-slate-500">Edit your hospital and contact details</p>
          </div>

          <div className="px-6 sm:px-8 py-6 sm:py-8">
            {saveSuccess && (
              <div className="mb-6 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200/80">
                <p className="text-sm font-medium text-emerald-800">Profile updated successfully!</p>
              </div>
            )}

            {saveError && (
              <div className="mb-6 p-3.5 rounded-xl bg-red-50 border border-red-200/80">
                <p className="text-sm font-medium text-red-700">{saveError}</p>
              </div>
            )}

            <form className="space-y-8" onSubmit={handleSave}>
              {/* Hospital information */}
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Building2 className="w-4 h-4 text-slate-500" /> Hospital Information
                </h2>

                <div className="space-y-2">
                  <label htmlFor="name" className={labelClass}>Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. City General Hospital"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                  <div className="space-y-2 shrink-0 w-fit">
                    <label htmlFor="phone_country_code" className={labelClass}>Phone country code</label>
                    <select
                      id="phone_country_code"
                      value={formData.phone_country_code}
                      onChange={(e) => handleInputChange('phone_country_code', e.target.value)}
                      className={`${selectClass} w-auto min-w-[5.5rem]`}
                    >
                      {COUNTRY_CODES.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2 flex-1 min-w-0">
                    <label htmlFor="phone" className={labelClass}>Phone *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        maxLength={10}
                        className={inputClass}
                        placeholder="10-digit phone number"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="address" className={labelClass}>Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-slate-400 w-4 h-4" />
                    <textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className={`${inputClass} min-h-[80px] py-3`}
                      placeholder="Street address"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="city" className={labelClass}>City</label>
                    <input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={inputClass}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="state" className={labelClass}>State</label>
                    <input
                      id="state"
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={inputClass}
                      placeholder="State"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="pincode" className={labelClass}>Pincode</label>
                    <input
                      id="pincode"
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => handleInputChange('pincode', e.target.value)}
                      maxLength={6}
                      className={inputClass}
                      placeholder="e.g. 411001"
                    />
                  </div>
                </div>
              </div>

              {/* Contact person */}
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
                  <User className="w-4 h-4 text-slate-500" /> Contact Person
                </h2>

                <div className="space-y-2">
                  <label htmlFor="contactPerson" className={labelClass}>Contact person name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="contactPerson"
                      type="text"
                      value={formData.contactPerson}
                      onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Dr. Sarah Smith"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                  <div className="space-y-2 shrink-0 w-fit">
                    <label htmlFor="contactPersonPhone_country_code" className={labelClass}>Contact phone country code</label>
                    <select
                      id="contactPersonPhone_country_code"
                      value={formData.contactPersonPhone_country_code}
                      onChange={(e) => handleInputChange('contactPersonPhone_country_code', e.target.value)}
                      className={`${selectClass} w-auto min-w-[5.5rem]`}
                    >
                      {COUNTRY_CODES.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2 flex-1 min-w-0">
                    <label htmlFor="contactPersonPhone" className={labelClass}>Contact person phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        id="contactPersonPhone"
                        type="tel"
                        value={formData.contactPersonPhone}
                        onChange={(e) => handleInputChange('contactPersonPhone', e.target.value)}
                        maxLength={10}
                        className={inputClass}
                        placeholder="10-digit phone number"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="contactPersonEmail" className={labelClass}>Contact person email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="contactPersonEmail"
                      type="email"
                      value={formData.contactPersonEmail}
                      onChange={(e) => handleInputChange('contactPersonEmail', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. sarah.smith@cityhospital.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onBackToProfile}
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-slate-700 hover:bg-slate-800 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <FloatingNavBar
        tabs={['Profile', 'Notifications', 'Edit Profile']}
        activeTab="Edit Profile"
        onTabChange={(tab) => {
          if (tab === 'Profile' && onBackToProfile) onBackToProfile();
        }}
        showLogout={false}
        className="mt-8"
      />
    </div>
  );
};

export default HospitalProfileEdit;

HospitalProfileEdit.propTypes = {
  onBackToProfile: PropTypes.func.isRequired
};
